import React, { useState, useMemo, useEffect } from 'react';
import useGameStore from '../store/gameStore';
import { formatMoney } from '../utils/format';
import { COMPETITORS, COMPETITION_EVENTS, simulateCompetitor, getAbroadDailyCost } from '../data/competitors';
import { CLIENT_SECTORS, CLIENT_TYPOLOGIES, ARGUMENTS } from '../data/clients';
import { localize, localizeByKey } from '../data/i18nData';
import { useTranslation } from '../i18n';
import {
  Users, TrendingUp, Trophy, Truck, Star, Swords, Shield,
  Handshake, AlertTriangle, ChevronRight, ChevronDown, Globe2,
  Eye, Lock, Target, Clock, Award, MapPin, Euro, Timer,
} from 'lucide-react';
import MarkdownLite from '../components/MarkdownLite';
import { getRelationTier, readRelationScore } from '../utils/competitorRelations';

const ARG_ICONS = { prix: Euro, délais: Timer, qualité: Award, proximité: MapPin }; // i18n-keep — ARGUMENTS data ids

/**
 * Avatar de concurrent — affiche le logo PNG s'il est défini, sinon
 * un cercle coloré avec les initiales (avatar). Préserve un rendu
 * cohérent pour les 4 concurrents du jeu ET les mentions hors-roster
 * (tenders, articles, etc.).
 */
function CompetitorAvatar({ comp, size = 40, className = '' }) {
  const [imgFailed, setImgFailed] = useState(false);
  const px = `${size}px`;
  if (comp.logo && !imgFailed) {
    return (
      <img
        src={comp.logo}
        alt={comp.name}
        onError={() => setImgFailed(true)}
        className={`rounded-full object-cover border-2 flex-shrink-0 ${className}`}
        style={{ width: px, height: px, borderColor: comp.color }}
        data-testid={`competitor-avatar-${comp.id}`}
      />
    );
  }
  return (
    <div
      className={`rounded-full flex items-center justify-center font-black text-foreground flex-shrink-0 ${className}`}
      style={{ width: px, height: px, backgroundColor: comp.color, fontSize: Math.round(size * 0.36) + 'px' }}
      data-testid={`competitor-avatar-${comp.id}-fallback`}
    >
      {comp.avatar}
    </div>
  );
}

/**
 * Chip + jauge de relation (Phase 1 du système Diplomatie).
 */
function RelationChip({ score, compact = false }) {
  const { t } = useTranslation();
  const tier = getRelationTier(score);
  const Icon = tier.icon;
  return (
    <div className={`inline-flex items-center gap-1.5 ${compact ? 'text-[10px]' : 'text-xs'}`} data-testid="relation-chip">
      <Icon className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} style={{ color: tier.color }} />
      <span className="font-semibold" style={{ color: tier.color }}>
        {t(tier.labelKey)}
      </span>
      {!compact && (
        <span className="text-slate-500 font-mono text-[10px]">{score}/100</span>
      )}
    </div>
  );
}

function CompetitorCard({ comp: rawComp, stats, playerRank, isExpanded, onToggle }) {
  const { t } = useTranslation();
  const comp = localize('competitor', rawComp);
  const competitorRelations = useGameStore(s => s.competitorRelations);
  const relationScore = readRelationScore(competitorRelations, comp.id);
  return (
    <div className="bg-ink-800 rounded-lg border border-ink-600 overflow-hidden" data-testid={`competitor-${comp.id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-3 hover:bg-ink-700/40 transition-colors">
        <CompetitorAvatar comp={comp} size={40} />
        <div className="flex-1 text-left min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-bold text-slate-100 truncate">{comp.name}</p>
            {stats.reputation > 70 && <Star className="w-3 h-3 text-warn fill-amber-400" />}
            <RelationChip score={relationScore} compact />
          </div>
          <p className="text-[10px] text-slate-500">{comp.slogan}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-bold" style={{ color: comp.color }}>{formatMoney(stats.ca)}/m</p>
          <p className="text-[10px] text-slate-500">{stats.fleet} {t('vehicles_short')} · {stats.drivers} {t('drivers_short')}</p>
        </div>
        {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 border-t border-ink-600 pt-2 space-y-2">
          <p className="text-xs text-slate-400 italic">"{comp.style}"</p>
          {/* 17/06/2026 (Lot 6 Phase 1.5) — Micro-bio du dirigeant fictif.
              Humanise la concurrence, donne le ton pour les futurs
              événements narratifs (OPA, fusion, sous-traitance). */}
          {comp.director && (
            <div
              className="bg-ink-700/40 border border-ink-600 rounded-lg p-2.5 flex gap-2.5"
              data-testid={`competitor-director-${comp.id}`}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-black text-white"
                style={{ backgroundColor: comp.color }}
              >
                {comp.director.name.split(' ').map(w => w[0]).slice(0, 2).join('')}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <p className="text-xs font-bold text-slate-200">{comp.director.name}</p>
                  <p className="text-[10px] text-slate-500">{comp.director.role}</p>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">{comp.director.bio}</p>
              </div>
            </div>
          )}
          {/* Jauge de relation détaillée — Phase 1 du système Diplomatie. */}
          <RelationGauge score={relationScore} />
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="bg-ink-700/30 rounded p-2 text-center">
              <p className="text-[10px] text-slate-500">CA mensuel</p>
              <p className="font-bold" style={{ color: comp.color }}>{formatMoney(stats.ca)}</p>
            </div>
            <div className="bg-ink-700/30 rounded p-2 text-center">
              <p className="text-[10px] text-slate-500">{t('reputation_short')}</p>
              <p className="font-bold text-slate-300">{stats.reputation}/100</p>
            </div>
            <div className="bg-ink-700/30 rounded p-2 text-center">
              <p className="text-[10px] text-slate-500">{t('comp_kpi_fleet')}</p>
              <p className="font-bold text-slate-300">{stats.fleet} {t('vehicles_short')}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1">
            {comp.specialties.map((s, i) => (
              <span key={`spec-${comp.id}-${s}`} className="text-[10px] px-1.5 py-0.5 rounded-full border text-slate-400" style={{ borderColor: comp.color + '40' }}>{s}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Jauge horizontale du score de relation (0-100). Le curseur indique
 * la position exacte ; les couleurs de fond reprennent les tiers.
 */
function RelationGauge({ score }) {
  const { t } = useTranslation();
  const tier = getRelationTier(score);
  return (
    <div className="space-y-1.5" data-testid="relation-gauge">
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-slate-500 uppercase tracking-wide">{t('relation_with_us')}</span>
        <RelationChip score={score} />
      </div>
      <div className="relative h-2 rounded-full overflow-hidden flex bg-ink-700">
        <div className="h-full" style={{ width: '16%', backgroundColor: '#DC2626' }} />
        <div className="h-full" style={{ width: '20%', backgroundColor: '#F97316' }} />
        <div className="h-full" style={{ width: '20%', backgroundColor: '#EAB308' }} />
        <div className="h-full" style={{ width: '20%', backgroundColor: '#3B82F6' }} />
        <div className="h-full" style={{ width: '24%', backgroundColor: '#10B981' }} />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-1 h-3 bg-white rounded-sm shadow-sm border border-black/20"
          style={{ left: `${Math.min(99, Math.max(0, score))}%` }}
        />
      </div>
    </div>
  );
}

export default function Marche() {
  const {
    totalDays, reputation, monthlyRevenues, vehicles, drivers, activeContracts,
    competitorEvents, pendingCompetitionEvent, resolveCompetitionEvent, notoriety, treasury,
    clientRoster, competitorContracts, challenges, challengeHistory, hasMarketIntelAccess,
    ensureMarketDataInitialized, challengeContract, acceptChallengedContract,
    getChallengesUsedThisMonth,
  } = useGameStore();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(null);
  const [tab, setTab] = useState('classement');
  const [challengeTarget, setChallengeTarget] = useState(null);
  const [challengeIntensity, setChallengeIntensity] = useState('standard');

  // Initialize the client roster + competitor contracts the first time the
  // player opens the page (after they unlock market intel).
  useEffect(() => {
    if (hasMarketIntelAccess && hasMarketIntelAccess()) {
      ensureMarketDataInitialized && ensureMarketDataInitialized();
    }
  }, [hasMarketIntelAccess, ensureMarketDataInitialized]);

  // Simulate all competitors
  const competitorStats = useMemo(() => {
    return COMPETITORS.map(c => ({
      ...c,
      stats: simulateCompetitor(c, totalDays, reputation, monthlyRevenues),
    }));
  }, [totalDays, reputation, monthlyRevenues]);

  const competitorRelations = useGameStore(s => s.competitorRelations);

  // Build ranking: player + competitors sorted by CA
  const ranking = useMemo(() => {
    const all = [
      { id: 'player', isPlayer: true, ca: monthlyRevenues, fleet: vehicles.length, reputation: Math.round(reputation), drivers: drivers.length, color: '#1F3864' },
      ...competitorStats.map(c => {
        const locC = localize('competitor', c);
        return {
          id: c.id,
          name: locC.name,
          isPlayer: false,
          ca: c.stats.ca,
          fleet: c.stats.fleet,
          reputation: c.stats.reputation,
          drivers: c.stats.drivers,
          color: c.color,
          // 17/06/2026 (Lot 6 Phase 1) — surface l'objet brut du concurrent
          // pour permettre l'affichage du logo + le score de relation.
          competitor: locC,
          relationScore: readRelationScore(competitorRelations, c.id),
        };
      }),
    ];
    return all.sort((a, b) => b.ca - a.ca);
  }, [competitorStats, monthlyRevenues, vehicles.length, reputation, drivers.length, competitorRelations]);

  const playerRank = ranking.findIndex(r => r.isPlayer) + 1;

  const abroadDailyCost = getAbroadDailyCost();
  const exportContracts = (activeContracts || []).filter(c => c.category === 'Export EU' || c.isExport);

  return (
    <div className="space-y-4" data-testid="marche-page">
      <h2 className="text-2xl font-bold text-foreground">{t('market_title')}</h2>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3">
          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Trophy className="w-3.5 h-3.5" /><span>{t('ranking')}</span></div>
          <p className={`text-lg font-bold ${playerRank === 1 ? 'text-warn' : 'text-foreground'}`}>#{playerRank}</p>
          <p className="text-[10px] text-slate-500">sur {ranking.length} entreprises</p>
        </div>
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3">
          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Swords className="w-3.5 h-3.5" /><span>{t('competitors_label')}</span></div>
          <p className="text-lg font-bold text-slate-300">{COMPETITORS.length}</p>
          <p className="text-[10px] text-slate-500">{t('active_on_market')}</p>
        </div>
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3">
          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Globe2 className="w-3.5 h-3.5" /><span>{t('export_contracts')}</span></div>
          <p className="text-lg font-bold text-purple-400">{exportContracts.length}</p>
          <p className="text-[10px] text-slate-500">{formatMoney(abroadDailyCost)}/j {t('daily_relocation_fee')}</p>
        </div>
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3">
          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Star className="w-3.5 h-3.5" /><span>{t('your_revenue_kpi')}</span></div>
          <p className="text-lg font-bold text-money">{formatMoney(monthlyRevenues)}/m</p>
          <p className="text-[10px] text-slate-500">{t('reputation_short')} : {Math.round(reputation)}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {[
          { id: 'classement', label: t('regional_ranking_tab'), icon: Trophy },
          { id: 'concurrents', label: t('competitors_label'), icon: Users },
          { id: 'veille', label: t('market_intel_tab'), icon: Eye, locked: !hasMarketIntelAccess() },
          { id: 'evenements', label: t('events_tab'), icon: Swords, badge: (competitorEvents || []).length },
        ].map(tabItem => (
          <button
            key={tabItem.id}
            onClick={() => !tabItem.locked && setTab(tabItem.id)}
            disabled={tabItem.locked}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
              tabItem.locked
                ? 'bg-ink-800 text-slate-600 border border-ink-700 cursor-not-allowed opacity-60'
                : tab === tabItem.id ? 'bg-accent text-white' : 'bg-ink-800 text-slate-400 border border-ink-600'
            }`}
            data-testid={`market-tab-${tabItem.id}`}
            title={tabItem.locked ? t('market_intel_unlock_tip') : undefined}
          >
            <tabItem.icon className="w-3.5 h-3.5" /> {tabItem.label}
            {tabItem.locked && <Lock className="w-3 h-3 ml-0.5" />}
            {tabItem.badge > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-danger/100 text-white text-[10px] font-bold">{tabItem.badge}</span>}
          </button>
        ))}
      </div>

      {/* CLASSEMENT */}
      {tab === 'classement' && (
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
          <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-warn" /> {t('regional_ranking_subtitle')}
          </h3>
          <div className="space-y-2">
            {ranking.map((r, i) => (
              <div
                key={r.id}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  r.isPlayer ? 'border-accent bg-accent/5' : 'border-ink-600'
                }`}
                data-testid={`ranking-row-${r.id}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  i === 0 ? 'bg-warn/100 text-white' : i === 1 ? 'bg-gray-400 text-white' : i === 2 ? 'bg-amber-700 text-white' : 'bg-ink-700 text-slate-400'
                }`}>
                  {i + 1}
                </div>
                {/* 17/06/2026 (Lot 6) — Logo concurrent dans le classement (bug fix
                    "logos ne s'affichent pas"). Joueur conserve juste son nom. */}
                {!r.isPlayer && r.competitor && (
                  <CompetitorAvatar comp={r.competitor} size={32} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-bold ${r.isPlayer ? 'text-foreground' : 'text-slate-200'}`}>
                      {r.isPlayer ? t('you') : r.name}
                    </p>
                    {!r.isPlayer && r.relationScore != null && (
                      <RelationChip score={r.relationScore} compact />
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500">{r.fleet} {t('vehicles_short')} · {r.drivers} {t('drivers_short')} · {t('rep_short')} {r.reputation}</p>
                </div>
                <p className="text-sm font-bold" style={{ color: r.color }}>{formatMoney(r.ca)}/m</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CONCURRENTS */}
      {tab === 'concurrents' && (
        <div className="space-y-3">
          {competitorStats.map(c => (
            <CompetitorCard
              key={c.id}
              comp={c}
              stats={c.stats}
              playerRank={playerRank}
              isExpanded={expanded === c.id}
              onToggle={() => setExpanded(expanded === c.id ? null : c.id)}
            />
          ))}
        </div>
      )}

      {/* VEILLE CONCURRENTS */}
      {tab === 'veille' && (
        <div className="space-y-3" data-testid="veille-tab">
          <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                <Eye className="w-4 h-4 text-accent" />
                {t('market_intel_title')}
              </h3>
              <div className="text-[10px] text-slate-500 font-mono">
                {t('market_intel_monthly_challenges')} <span className="text-warn font-bold">{getChallengesUsedThisMonth ? getChallengesUsedThisMonth() : 0}/3</span>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 leading-snug mb-3">
              {t('market_intel_intro')}
            </p>

            {/* Pending challenges */}
            {challenges && challenges.length > 0 && (
              <div className="mb-3">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">{t('market_intel_pending_label')}</p>
                <div className="space-y-1.5">
                  {challenges.map(ch => {
                    const client = (clientRoster || []).find(c => c.id === ch.clientId);
                    const arg = ARGUMENTS.find(a => a.id === ch.argument);
                    return (
                      <div key={ch.id} className="flex items-center justify-between bg-ink-700/30 border border-ink-600 rounded px-3 py-2 text-xs" data-testid={`challenge-${ch.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-200 truncate">{client ? client.name : '—'}</p>
                          <p className="text-[10px] text-slate-500">
                            vs {ch.competitorName} · {t('market_intel_argument_label')} {arg?.label || ch.argument}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {ch.status === 'pending' && (
                            <span className="text-[10px] text-warn font-mono uppercase">
                              <Clock className="w-3 h-3 inline mr-0.5" />
                              {t('market_intel_in_progress', { days: Math.max(0, ch.endDay - totalDays) })}
                            </span>
                          )}
                          {ch.status === 'success' && (
                            <button
                              onClick={() => acceptChallengedContract(ch.id)}
                              className="text-[10px] bg-money text-ink-900 px-2 py-1 rounded font-bold uppercase tracking-wider"
                              data-testid={`accept-challenge-${ch.id}`}
                            >
                              {t('market_intel_capture', { prob: ch.successProb })}
                            </button>
                          )}
                          {ch.status === 'failed' && (
                            <span className="text-[10px] text-danger font-mono uppercase">{t('market_intel_failure', { prob: ch.successProb })}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Active competitor contracts */}
            {(!competitorContracts || competitorContracts.length === 0) ? (
              <p className="text-xs text-slate-500 italic text-center py-4">{t('market_intel_empty')}</p>
            ) : (
              <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1">
                {(competitorContracts || []).map(cc => {
                  const client = (clientRoster || []).find(c => c.id === cc.clientId);
                  if (!client) return null;
                  const sector = CLIENT_SECTORS.find(s => s.id === client.sectorId);
                  const typo = CLIENT_TYPOLOGIES.find(tp => tp.id === client.typologyId);
                  return (
                    <div key={cc.id} className="border border-ink-600 bg-ink-700/30 rounded-lg p-3" data-testid={`competitor-contract-${cc.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="text-xs font-bold text-slate-100 truncate">{client.name}</p>
                            <span className="text-[10px] px-1 py-0.5 rounded font-mono" style={{ backgroundColor: sector?.color + '20', color: sector?.color }}>
                              {sector?.name || client.sectorId}
                            </span>
                            <span className="text-[10px] px-1 py-0.5 rounded bg-ink-700 text-slate-400 font-mono">
                              {typo?.name || ''}
                            </span>
                          </div>
                          <p className="text-[10px] text-slate-500 mt-0.5">
                            {t('market_intel_competitor_label')} <span className="text-warn font-mono">{cc.competitorName}</span>
                            <span className="mx-1.5 text-slate-600">·</span>
                            {t('market_intel_loyalty_label')} <span className={`font-bold font-mono ${client.loyalty > 70 ? 'text-danger' : client.loyalty > 50 ? 'text-warn' : 'text-money'}`}>{client.loyalty}/100</span>
                            <span className="mx-1.5 text-slate-600">·</span>
                            J-{cc.daysRemaining}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-1">
                            {t('market_intel_primary_arg')} <span className="text-accent">{client.primaryDriver}</span> · {t('market_intel_secondary_arg')} <span className="text-accent/70">{client.secondaryDriver}</span>
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-bold text-money font-mono">{formatMoney(cc.value)}/j</p>
                          <button
                            onClick={() => setChallengeTarget(cc)}
                            disabled={treasury < 800 || (getChallengesUsedThisMonth ? getChallengesUsedThisMonth() : 0) >= 3}
                            className="mt-1 text-[10px] bg-accent text-white px-2 py-1 rounded font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                            data-testid={`challenge-btn-${cc.id}`}
                            title={treasury < 800 ? t('market_intel_low_treasury_800') : (getChallengesUsedThisMonth && getChallengesUsedThisMonth() >= 3) ? t('market_intel_monthly_quota_reached') : t('market_intel_launch_challenge')}
                          >
                            <Target className="w-3 h-3" /> {t('market_intel_challenge_btn')}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* HISTORIQUE DES DÉFIS */}
          {challengeHistory && challengeHistory.length > 0 && (
            <div className="bg-ink-800 rounded-lg border border-ink-600 p-4" data-testid="challenge-history-panel">
              <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                <Target className="w-4 h-4 text-slate-400" /> {t('market_intel_history_title')}
              </h3>
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1">
                {[...challengeHistory].reverse().slice(0, 10).map(h => {
                  const won = h.status === 'success';
                  return (
                    <div key={h.id + '-' + (h.resolvedAt || 0)} className={`flex items-center justify-between p-2 rounded-md border ${won ? 'border-money/30 bg-money/5' : 'border-danger/30 bg-danger/5'}`} data-testid={`history-item-${h.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-slate-100 truncate">{h.clientName || '—'}</p>
                        <p className="text-[10px] text-slate-500">
                          vs <span className="text-warn">{h.competitorName}</span>
                          <span className="mx-1 text-slate-600">·</span>
                          {h.intensity === 'intensive' ? t('market_intel_intensity_intensive') : t('market_intel_intensity_standard')}
                          <span className="mx-1 text-slate-600">·</span>
                          {t('market_intel_chance')} {h.successProb}%
                        </p>
                      </div>
                      <div className={`text-right ${won ? 'text-money' : 'text-danger'}`}>
                        <p className="text-xs font-bold uppercase font-mono">{won ? t('market_intel_won') : t('market_intel_lost')}</p>
                        <p className="text-[10px] opacity-80">J{h.resolvedAt || h.day}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {(() => {
                const total = challengeHistory.length;
                const won = challengeHistory.filter(h => h.status === 'success').length;
                const rate = total > 0 ? Math.round((won / total) * 100) : 0;
                return (
                  <p className="text-[10px] text-slate-500 mt-2 text-center" data-testid="challenge-success-rate">
                    {t('market_intel_success_rate')} <strong className="text-slate-300">{rate}%</strong> ({won}/{total})
                  </p>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* CHALLENGE MODAL */}
      {challengeTarget && (() => {
        const client = (clientRoster || []).find(c => c.id === challengeTarget.clientId);
        if (!client) return null;
        return (
          <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="challenge-modal">
            <div className="bg-ink-800 rounded-xl w-full max-w-md shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200">
              <div className="bg-accent px-5 py-3">
                <p className="text-blue-200 text-xs font-medium uppercase tracking-wider">{t('challenge_modal_title')}</p>
                <h3 className="text-foreground font-bold text-base">{client.name}</h3>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-[12px] text-slate-400 leading-snug">
                  <MarkdownLite as="span" text={t('challenge_modal_intro', { client: client.name, competitor: challengeTarget.competitorName })} />
                </p>
                <div className="rounded-md border border-ink-600 bg-ink-700/30 p-2.5 text-xs">
                  <p className="text-[10px] text-slate-500 uppercase">{t('challenge_modal_profile')}</p>
                  <p className="text-slate-300">{t('market_intel_loyalty_label')} <span className="font-bold text-warn">{client.loyalty}/100</span> · {t('challenge_modal_sensitive_to')} <span className="font-bold text-accent">{client.primaryDriver}</span> {t('challenge_modal_and')} <span className="font-bold text-accent/70">{client.secondaryDriver}</span></p>
                </div>

                {/* Intensity selector */}
                <div className="grid grid-cols-2 gap-1.5" data-testid="challenge-intensity-selector">
                  <button
                    onClick={() => setChallengeIntensity('standard')}
                    className={`p-2 rounded-md border-2 text-xs ${challengeIntensity === 'standard' ? 'border-accent bg-accent/10' : 'border-ink-600 bg-ink-700/30'}`}
                    data-testid="intensity-standard"
                  >
                    <p className="font-bold text-slate-100">{t('challenge_intensity_standard')}</p>
                    <p className="text-[10px] text-slate-500">{t('challenge_intensity_standard_desc')}</p>
                  </button>
                  <button
                    onClick={() => setChallengeIntensity('intensive')}
                    disabled={treasury < 1600}
                    className={`p-2 rounded-md border-2 text-xs ${challengeIntensity === 'intensive' ? 'border-money bg-money/10' : 'border-ink-600 bg-ink-700/30'} disabled:opacity-40 disabled:cursor-not-allowed`}
                    data-testid="intensity-intensive"
                    title={treasury < 1600 ? t('market_intel_low_treasury_1600') : ''}
                  >
                    <p className="font-bold text-slate-100">{t('challenge_intensity_intensive')}</p>
                    <p className="text-[10px] text-money">{t('challenge_intensity_intensive_desc')}</p>
                  </button>
                </div>

                <div className="space-y-1.5">
                  {ARGUMENTS.map(a => {
                    const Icon = ARG_ICONS[a.id] || Target;
                    const isPrimary = a.id === client.primaryDriver;
                    const isSecondary = a.id === client.secondaryDriver;
                    return (
                      <button
                        key={a.id}
                        onClick={() => {
                          const r = challengeContract(challengeTarget.id, a.id, challengeIntensity);
                          if (r && r.ok) {
                            setChallengeTarget(null);
                            setChallengeIntensity('standard');
                          }
                          else if (r && r.reason === 'insufficient_funds') alert(t('market_intel_alert_low_treasury'));
                          else if (r && r.reason === 'monthly_cap') alert(t('market_intel_monthly_quota_reached'));
                        }}
                        className={`w-full text-left p-2.5 rounded-md border-2 transition-all ${
                          isPrimary ? 'border-money/50 bg-money/5 hover:border-money'
                          : isSecondary ? 'border-accent/50 bg-accent/5 hover:border-accent'
                          : 'border-ink-600 bg-ink-700/30 hover:border-ink-500'
                        }`}
                        data-testid={`argument-${a.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-slate-300" />
                            <span className="text-sm font-bold text-slate-100">{a.label}</span>
                            {isPrimary && <span className="text-[10px] px-1 rounded bg-money/30 text-money font-mono uppercase">{t('argument_strongest')}</span>}
                            {isSecondary && <span className="text-[10px] px-1 rounded bg-accent/30 text-accent font-mono uppercase">{t('argument_medium')}</span>}
                          </div>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-0.5">{a.desc}</p>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-500 italic">
                  {challengeIntensity === 'intensive'
                    ? t('challenge_cost_intensive')
                    : t('challenge_cost_standard')}
                </p>
              </div>
              <div className="px-5 py-3 border-t border-ink-600 bg-ink-700/30 flex justify-end">
                <button onClick={() => setChallengeTarget(null)} className="text-xs text-slate-400 hover:text-slate-200 px-3 py-2 transition-colors">
                  {t('cancel')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* COÛTS EXPORT — déplacé vers Finance > onglet « Coûts export ». */}

      {/* ÉVÉNEMENTS CONCURRENTIELS */}
      {tab === 'evenements' && (
        <div className="space-y-3">
          {/* Pending competition event */}
          {pendingCompetitionEvent && (
            <div className="bg-warn/10 border-2 border-warn/60 rounded-lg p-4" data-testid="pending-comp-event">
              <div className="flex items-center gap-2 mb-2">
                <Swords className="w-5 h-5 text-warn" />
                <p className="text-sm font-bold text-amber-800">{pendingCompetitionEvent.title}</p>
              </div>
              <p className="text-xs text-slate-300 mb-3">{pendingCompetitionEvent.description}</p>
              <div className="space-y-2">
                {pendingCompetitionEvent.choices.map((choice, i) => (
                  <button
                    key={choice.label}
                    onClick={() => resolveCompetitionEvent(i)}
                    className={`w-full text-left p-3 rounded-lg border-2 text-xs transition-all hover:${
                      choice.recommended ? 'border-money/60 bg-money/10' : 'border-ink-600 hover:border-ink-500'
                    }`}
                    data-testid={`comp-event-choice-${i}`}
                  >
                    <p className="font-semibold text-slate-200">{choice.label}</p>
                    <p className="text-slate-500 mt-0.5">{choice.description}</p>
                    {choice.cost > 0 && <p className="text-danger mt-0.5">{t('event_cost_label')} {formatMoney(choice.cost)}</p>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* History */}
          <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
            <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
              <Swords className="w-4 h-4" /> {t('market_competition_history_title')}
            </h3>
            {(competitorEvents || []).length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-4">{t('no_event_active')}</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {(competitorEvents || []).slice().reverse().map((e) => (
                  <div key={`${e.day}-${e.title}`} className="flex items-start gap-2 p-2 rounded-lg bg-ink-700/30 text-xs">
                    <span className="text-[10px] text-slate-500 w-10 flex-shrink-0">J{e.day}</span>
                    <div>
                      <p className="font-medium text-slate-300">{e.title}</p>
                      <p className="text-slate-500">{e.outcome}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Active reactions */}
          <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
            <h3 className="text-sm font-semibold text-slate-200 mb-3">{t('market_competitor_activity_title')}</h3>
            <div className="space-y-2">
              {competitorStats.map(c => {
                const reaction = monthlyRevenues > c.stats.ca * 1.2 ? c.reactions.playerGrows
                  : monthlyRevenues < c.stats.ca * 0.5 ? c.reactions.playerStruggles
                  : c.reactions.neutral;
                return (
                  <div key={c.id} className="flex items-start gap-2 p-2 rounded-lg bg-ink-700/30">
                    {c.logo ? (
                      <img src={c.logo} alt={c.name} className="w-6 h-6 rounded-full object-cover flex-shrink-0 border" style={{ borderColor: c.color }} />
                    ) : (
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-foreground flex-shrink-0" style={{ backgroundColor: c.color }}>
                        {c.avatar}
                      </div>
                    )}
                    <p className="text-[10px] text-slate-400">{reaction}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
